import { useEffect, useRef, useState, useReducer } from 'react'
import Dropzone, { IDropzoneProps } from 'react-dropzone-uploader'
import { Button, Stack, Typography, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import enGB from 'date-fns/locale/en-GB'
import enDE from 'date-fns/locale/de'
import { etlAPI } from '../../../shared/api/ETLAPI'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { setEtlFiles, setLatestEtlStatus, setFileMappings } from '../../../store/actions'
import FsContainer from '../common/Container'
import FsFileMappings from '../common/FileMappings'
import { formatDate } from '../../../shared/utils'
import FsAlert, { FsAlertRef } from '../../common/atoms/Alert'
import { ReactComponent as UploadIcon } from '../../../assets/etl/cloud_icon.svg'

interface FsUploadProps {
  setIsLoading: (isLoading: boolean) => void
  goBack?: () => void
}

const FsDataUpload = (props: FsUploadProps) => {
  const { t, i18n } = useTranslation()
  const { setIsLoading, goBack } = props
  const [startValue, setStartValue] = useState<Date | null>(null)
  const { runs } = useAppSelector((state) => state.currentAnalysis)
  const theme = useTheme()
  const dispatch = useAppDispatch()
  const { etlFiles, latestEtlStatus, etlFileMappings } = useAppSelector((state) => state.currentEtl)
  const defaultLocale = i18n.language === 'en' ? enGB : enDE
  const etlFileNames = etlFiles ? Object.keys(etlFiles) : []
  const alertRef = useRef<FsAlertRef>(null)
  const [uploadErr, setUploadErr] = useState('')
  const [dateErr, setDateErr] = useState('')
  const [, forceUpdate] = useReducer((x) => x + 1, 0)

  useEffect(() => {
    if (latestEtlStatus) {
      dispatch(setEtlFiles({ runId: latestEtlStatus.id }))
      setStartValue(new Date(latestEtlStatus.dateEffective))
    }
  }, [latestEtlStatus])

  const handleSubmit: IDropzoneProps['onSubmit'] = (_, allFiles) => {
    if (startValue) {
      setIsLoading(true)
      const defaultLocaleDate = i18n.language === 'en' ? 'en' : 'de'
      const formattedCorrespondanceDate = startValue
        ? formatDate(startValue, defaultLocaleDate, 'dd.mm.yyyy')
        : null
      const isAnalysisDone =
        runs &&
        runs.filter(
          (r) =>
            formatDate(r.runAt, defaultLocaleDate, 'dd.mm.yyyy') === formattedCorrespondanceDate,
        )
      if (isAnalysisDone && isAnalysisDone.length > 0) {
        setIsLoading(false)
        return
      }
      if (etlFileNames) {
        const allFileNames = [...etlFileNames, ...allFiles.map((f) => f.meta.name)]
        const duplicates = new Set(
          allFileNames.filter((item, index) =>
            allFileNames.some((elem, idx) => elem === item && idx !== index),
          ),
        )
        if (duplicates.size > 0) {
          setIsLoading(false)
          setUploadErr(
            t('upload.duplicate.error', { duplicates: Array.from(duplicates).join(', ') }),
          )
          return
        }
      }
      etlAPI
        .uploadFiles(
          allFiles.map((f) => f.file),
          startValue,
          latestEtlStatus?.id ?? null,
        )
        .then((res) => {
          if (res.status === 201) {
            dispatch(setEtlFiles({ runId: res.data.run }))
            dispatch(setLatestEtlStatus())
            alertRef.current?.openAlert({
              message: t('file.upload.success'),
            })
            allFiles.forEach((f) => f.remove())
          }
        })
        .catch((err) => {
          if (err.status === 413) {
            alertRef.current?.openAlert({
              severity: 'error',
              message: t('file.upload.toobig.etl'),
            })
          } else {
            alertRef.current?.openAlert({
              severity: 'error',
              message: t('file.upload.error'),
            })
          }
        })
        .finally(() => {
          setIsLoading(false)
          setUploadErr('')
        })
    }
  }

  const mapFiles = async () => {
    setIsLoading(true)
    await dispatch(setFileMappings({ mappings: etlFileMappings }))
    setIsLoading(false)
  }

  return (
    <FsContainer
      footer={
        <>
          <Button variant="outlined" onClick={goBack}>
            {t('back')}
          </Button>
          {etlFileNames.length > 0 && (
            <Button
              variant="contained"
              onClick={mapFiles}
              sx={{ ml: 2 }}
              disabled={!!uploadErr || !!dateErr}
            >
              {t('send.data')}
            </Button>
          )}
        </>
      }
    >
      <Typography variant="h3">{t('corresponding.to')}</Typography>
      <Typography my={2}>{t('select.upload.date')}</Typography>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={defaultLocale}>
        <DatePicker
          disableFuture
          label={`${t('data.from')} *`}
          views={['year', 'month', 'day']}
          value={startValue}
          maxDate={new Date()}
          onChange={(newValue: any) => {
            if (newValue) {
              setStartValue(newValue)
              const defaultLocaleDate = i18n.language === 'en' ? 'en' : 'de'
              const formattedCorrespondanceDate = newValue
                ? formatDate(newValue, defaultLocaleDate, 'dd.mm.yyyy')
                : null
              const isAnalysisDone =
                runs &&
                runs.filter(
                  (r) =>
                    formatDate(r.runAt, defaultLocaleDate, 'dd.mm.yyyy') ===
                    formattedCorrespondanceDate,
                )
              if (isAnalysisDone && isAnalysisDone.length > 0) {
                setDateErr(t('upload.date.error'))
              } else {
                setDateErr('')
              }
            }
          }}
          slotProps={{
            field: {
              readOnly: true,
            },
            textField: {
              helperText: dateErr,
              error: !!dateErr,
            },
          }}
          disabled={(etlFileNames.length ?? 0) > 0}
        />
      </LocalizationProvider>
      <Typography variant="h3" my={2}>
        {etlFileNames.length > 0 ? t('upload.more.files') : t('upload.your.files')}
      </Typography>
      <Typography>{t('upload.files.info.etl')}</Typography>
      {uploadErr && (
        <Typography color="error" variant="body2" className="font-semibold">
          {uploadErr}
        </Typography>
      )}
      <Dropzone
        disabled={!startValue || dateErr !== ''}
        onSubmit={handleSubmit}
        accept="text/csv,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
        styles={{
          dropzone: {
            marginTop: 8,
            minHeight: 200,
            paddingLeft: 20,
            paddingRight: 20,
          },
          inputLabel: {
            padding: '16px 172px',
            color: theme.palette.primary.main,
            fontSize: 16,
            textAlign: 'center',
            fontFamily: 'Raleway',
          },
          preview: {
            backgroundColor:
              theme.palette.mode === 'dark'
                ? theme.palette.text.secondary
                : theme.palette.action.hover,
            marginTop: 16,
            borderRadius: 4,
            paddingTop: 4,
            paddingBottom: 4,
          },
          submitButton: {
            backgroundColor: theme.palette.primary.main,
            padding: '12px 24px',
            fontFamily: 'Raleway',
            fontSize: '14px',
          },
          inputLabelWithFiles: {
            border: `1px solid ${theme.palette.primary.main}`,
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.primary.main,
            padding: '8px 16px',
            marginLeft: '0px',
            fontFamily: 'Raleway',
            fontSize: '14px',
          },
        }}
        submitButtonContent={t('file.upload.submit')}
        inputWithFilesContent={t('add.more.files')}
        multiple
        inputContent={
          <Stack alignItems="center">
            <UploadIcon />
            <Typography variant="h3" textAlign="center" mt={2}>
              {t(!startValue || dateErr !== '' ? 'file.drag.drop.before.date' : 'file.drag.drop')}
            </Typography>
          </Stack>
        }
        onChangeStatus={(_, status) => {
          if (status === 'rejected_file_type') {
            alertRef.current?.openAlert({
              message: t('file.upload.toobig.etl'),
              severity: 'error',
            })
          } else {
            forceUpdate()
          }
        }}
      />
      <FsFileMappings isInternalUser={false} setIsLoading={setIsLoading} />
      <FsAlert ref={alertRef} />
    </FsContainer>
  )
}

export default FsDataUpload
